import { Box, Link, Stack, useMediaQuery } from '@mui/material';
import Button from '@web-packages/atoms/Button';
import TextInput from '@web-packages/atoms/TextInput';
import { useCallback, useEffect, useState } from 'react';
import CardShell from '../CardShell';
import { defaultFooterSocialButtons, defaultFooterLinks } from './defaults';

export type LoginProps = {
  title?: string;
  description?: string;
  emailLabel?: string;
  emailValue?: string;
  onEmailChange?: (value: string) => void;
  helpLinkText?: string;
  onHelpClick?: () => void;
  secondaryButtonLabel?: string;
  onSecondaryClick?: () => void;
  primaryButtonLabel?: string;
  onPrimaryClick?: () => void;
  isPrimaryLoading?: boolean;
  emailErrorText?: string;
};

const defaultDescription =
  'På Språkservice Online kan du boka tolk, beställa översättning och hantera dina bokningar. Språkservice Online kräver inga förkunskaper och vägleder dig steg för steg genom hela bokningsprocessen.';

/** @deprecated Use AUTH_CARD_MAX_WIDTH_PX from CardShell instead. */
export const LOGIN_CARD_MAX_WIDTH_PX = 1040;

const Login = (props: LoginProps) => {
  const {
    title = 'Logga in',
    description = defaultDescription,
    emailLabel = 'E-postadress',
    emailValue = '',
    onEmailChange,
    helpLinkText = 'Behöver du hjälp med att logga in?',
    onHelpClick,
    secondaryButtonLabel = 'Bli kund',
    onSecondaryClick,
    primaryButtonLabel = 'Nästa',
    onPrimaryClick,
    isPrimaryLoading = false,
    emailErrorText,
  } = props;

  const isDesktop = useMediaQuery('(min-width:1040px)');
  const isEmailControlled = onEmailChange !== undefined;
  const [uncontrolledEmail, setUncontrolledEmail] = useState(() => emailValue ?? '');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (isEmailControlled) return;
    setUncontrolledEmail(emailValue ?? '');
  }, [emailValue, isEmailControlled]);

  const emailForInput = isEmailControlled ? (emailValue ?? '') : uncontrolledEmail;

  const handleEmailChange = (value: string) => {
    if (emailError) setEmailError(undefined);
    if (isEmailControlled) {
      onEmailChange(value);
    } else {
      setUncontrolledEmail(value);
    }
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePrimaryClick = useCallback(() => {
    if (!isValidEmail(emailForInput)) {
      setEmailError('Ange en giltig e-postadress');
      return;
    }
    setLocalLoading(true);
    setTimeout(() => {
      onPrimaryClick?.();
      setLocalLoading(false);
    }, 600);
  }, [emailForInput, onPrimaryClick]);

  const isLoading = localLoading || isPrimaryLoading;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Enter' && !isLoading && (e.target as HTMLElement).tagName !== 'BUTTON') handlePrimaryClick(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePrimaryClick]);

  const startLoading = useCallback((performAction: () => void) => {
    setLocalLoading(true);
    setTimeout(() => {
      performAction?.();
      setLocalLoading(false);
    }, 600);
  }, []);

  return (
    <form onSubmit={(e) => { e.preventDefault(); handlePrimaryClick(); }}>
      <CardShell
        title={title}
        description={description}
        footerSocialButtons={defaultFooterSocialButtons}
        footerLinks={defaultFooterLinks}
        loading={isLoading}
        rightContent={
          <Stack spacing={1} sx={{ width: '100%', pt: '9px' }}>
            <TextInput
              id="login-email"
              label={emailLabel}
              type="email"
              value={emailForInput}
              onChange={handleEmailChange}
              errorText={emailError || emailErrorText}
              fullWidth
              maxLength={254}
              showAllText
              disabled={isLoading}
            />
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Link
                href="#"
                onClick={(e) => { e.preventDefault(); startLoading(() => onHelpClick?.()); }}
                sx={{
                  color: 'secondary.main',
                  fontWeight: 'normal',
                  fontSize: '14px',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  padding: '6px 8px',
                  margin: '-6px -8px',
                  '&:hover': { textDecoration: 'none', backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                {helpLinkText}
              </Link>
            </Box>
          </Stack>
        }
        actions={
          <Stack direction="row" justifyContent={isDesktop ? 'flex-end' : 'space-between'} spacing={2}>
            <Stack sx={{ ml: { xs: '-25px !important', md: 0 } }}>
              <Button
                type="button"
                variant="text"
                color="primary"
                label={secondaryButtonLabel}
                onClick={() => { onSecondaryClick?.(); }}
                fullWidthOnMobile={false}
              />
            </Stack>
            <Button
              variant="contained"
              color="primary"
              shape="round"
              label={primaryButtonLabel}
              onClick={handlePrimaryClick}
              type="submit"
              disabled={isLoading}
              fullWidthOnMobile={false}
            />
          </Stack>
        }
      />
    </form>
  );
};

export default Login;
